import { useState } from 'react';
import styles from './Schedule.module.css';
import shared from '../HomePage.module.css';
import useSchedule from '../hooks/useSchedule.js';
import ExplanationApplyModal from './ExplanationApplyModal.jsx';

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function EventChip({ e }) {
  return (
    <>
      <span className={cx(styles.calTag, styles[e.mode])}>{e.mode === 'on' ? '비대면' : '대면'}</span>
      {e.t}
      {e.time ? <span className={styles.calTime}>{e.time}</span> : null}
    </>
  );
}

/**
 * ═══ INFO SESSION SCHEDULE ═══
 * hooks/useSchedule.js가 loadSchedule()에서 불러온 데이터를 월별 달력 격자 + 모바일
 * 아젠다용 구조로 가공해 반환한다. 원본은 DOM을 직접 생성했지만, 여기서는 동일한 계산
 * 결과를 선언적으로 렌더링한다. 오늘 날짜 기준 현재 달이 기본으로 열리고, 9~12월처럼
 * 일정이 없는 달은 안내 패널(calEmpty)로 대체된다(showEmptyPanel).
 */
export default function Schedule() {
  const { month, year, setMonth, showEmptyPanel, grid, agenda } = useSchedule();
  const [selectedEvent, setSelectedEvent] = useState(null);

  // 캘린더/아젠다에서 일정 카드를 클릭하면 그 회차 정보(지역/일시/대면·비대면)를
  // 신청서 팝업에 그대로 넘긴다 — month/year는 useSchedule이 관리하는 현재 보기 상태라
  // 이벤트 객체 자체(d/mode/t/time/place)에는 없어서 여기서 합쳐준다.
  const openEventModal = (e) => setSelectedEvent({ ...e, month, year });

  return (
    <section className={`${shared.section} ${styles.schedule}`} id="schedule">
      <div className="wrap">
        <div className={`${shared.secHead} ${shared.reveal}`}>
          <span className={shared.eyebrow}>Info Session</span>
          <h2>
            잉글리시팝 <span className={shared.hl}>사업 설명회</span> 일정
          </h2>
          <p>
            가맹 창업이 궁금하다면, 가까운 일정에 참여해 직접 확인하세요.
            <br />
            매월 대면·비대면 설명회를 운영합니다.
          </p>
        </div>

        {/*
          버그 수정: shared.reveal(스크롤 등장 페이드인)은 IntersectionObserver가
          최초 1회 노출 시 실제 DOM에 "in" 클래스를 직접 붙이고(classList.add) 즉시
          unobserve 해버린다. 이 .in 클래스는 React state가 아니라 DOM에 직접 붙인
          것이라, React가 모르는 변경이다. 그런데 예전 코드는 shared.reveal을
          showEmptyPanel(월별로 계속 바뀌는 조건부 클래스)과 같은 div에 함께 넣어뒀다.
          그래서 달(月)을 옮겨 showEmptyPanel이 바뀌면 React가 그 div의 className
          문자열이 달라졌다고 판단해 className을 통째로 다시 써버리고, 그 순간
          DOM에 붙어있던 .in 클래스가 사라진다. 옵저버는 이미 한 번 발동 후
          unobserve된 상태라 다시 붙지 않고, 결과적으로 opacity:0 상태(.reveal의
          기본값)로 영영 남아 "달력 전체가 사라진 것처럼" 보이는 버그였다.
          → 해결: shared.reveal은 이 바깥 래퍼(항상 고정된 클래스 하나)에만 붙이고,
          월별로 바뀌는 isEmptyMonth 조건부 클래스는 안쪽 calWrap에서 따로 관리한다.
        */}
        <div className={shared.reveal}>
          <div className={cx(styles.calWrap, showEmptyPanel && styles.isEmptyMonth)}>
            <div className={styles.calMonths} role="tablist" aria-label="월 선택">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={cx(styles.calMonth, month === m && styles.isActive)}
                  role="tab"
                  aria-selected={month === m}
                  onClick={() => setMonth(m)}
                >
                  {m}
                  <span className={styles.moKo}>월</span>
                </button>
              ))}
            </div>

            <div className={styles.calHead} aria-hidden="true">
              <span className={styles.sun}>SUN</span>
              <span>MON</span>
              <span>TUE</span>
              <span>WED</span>
              <span>THU</span>
              <span>FRI</span>
              <span className={styles.sat}>SAT</span>
            </div>

            <div className={styles.calGrid}>
              {grid.map((cell) => (
                <div
                  key={cell.key}
                  className={cx(
                    styles.calCell,
                    cell.isSun && styles.sun,
                    cell.isSat && styles.sat,
                    cell.isEmpty && styles.isEmpty,
                    cell.isToday && styles.isToday
                  )}
                >
                  {!cell.isEmpty && (
                    <>
                      <div className={styles.calDate}>{cell.dayNum}</div>
                      {cell.events.map((e, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={styles.calEvent}
                          onClick={() => openEventModal(e)}
                        >
                          <EventChip e={e} />
                        </button>
                      ))}
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.calAgenda}>
              {agenda.length === 0 ? (
                <div className={styles.calAgendaEmpty}>해당 월에 예정된 설명회가 없습니다.</div>
              ) : (
                agenda.map((item) => (
                  <div key={item.day} className={styles.calAgendaItem}>
                    <div className={styles.calAgendaDate}>
                      <b>{item.day}</b>
                      <span>
                        {month}월 ({item.weekday})
                      </span>
                    </div>
                    <div className={styles.calAgendaBody}>
                      {item.events.map((e, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={styles.calEvent}
                          onClick={() => openEventModal(e)}
                        >
                          <EventChip e={e} />
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* 일정 없는 달(9월 이후 등) 안내 패널 — isEmptyMonth일 때만 표시 */}
            <div className={styles.calEmpty}>
              <div className={styles.ico}>🗓️</div>
              <h4>등록된 일정이 없습니다</h4>
              <p>
                현재 예정된 설명회 일정이 없습니다.
                <br />
                가맹 상담이 필요하시면 아래 번호로 문의해 주세요.
              </p>
              <a href="tel:1660-0540" className={`${shared.btn} ${shared.btnPrimary}`}>
                잉글리시팝 대표번호 : 1660-0540
              </a>
            </div>
          </div>
        </div>

        <div className={`${styles.calLegend} ${shared.reveal}`}>
          <i>
            <span className={cx(styles.dot, styles.off)}></span>대면 설명회
          </i>
          <i>
            <span className={cx(styles.dot, styles.on)}></span>비대면(온라인) 설명회
          </i>
        </div>
        <p className={`${styles.calNote} ${shared.reveal}`}>
          * 상기 일정은 신청 인원에 따라 변경될 수 있으며, 변경 시 신청하신 연락처로 미리 안내드립니다.
        </p>
      </div>

      <ExplanationApplyModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </section>
  );
}
